import { useTranslation } from "react-i18next";
import { DownloadIcon, Trash, Plus } from "lucide-react";
import { useMemo, useState } from "react";
import {
  useGetUserImageGenerators,
  useDeleteImageGenerator,
} from "@packages/api/madlenAPI";
import { format } from "date-fns";
import { enUS, tr } from "date-fns/locale";
import { ImageGeneratorResponseDto } from "@packages/api/madlenAPI.schemas";
import CreateImage from "../../components/imagen/CreateImage";
import ImagePreviewModal from "../../components/imagen/ImagePreviewModal";
import { Text, Button } from "@core/index";
import DeleteConfirmPopUp from "@features/tools/components/tool_components/DeleteConfirmPopUp";
import { useToast } from "@packages/contexts/ToastContext";
import { useQueryClient } from "@tanstack/react-query";
import ToolHelper from "@features/tools/components/ToolHelper";
import ErrorComponent from "@features/tools/components/error-component";

export const ImageGeneratorView = () => {
  const { t, i18n } = useTranslation();
  const [selectedImage, setSelectedImage] =
    useState<ImageGeneratorResponseDto | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [showDeletePopup, setShowDeletePopup] = useState<number | undefined>();
  const { addToast } = useToast();
  const queryClient = useQueryClient();

  const {
    data: allImages,
    isLoading,
    error,
    refetch,
  } = useGetUserImageGenerators(
    {
      size: 100,
    },
    {
      query: {
        refetchInterval: (query) => {
          const hasProcessingItems = query.state.data?.data?.some((item) => {
            return (
              item.status !== "READY" ||
              !item.images ||
              item.images.length === 0
            );
          });

          return hasProcessingItems ? 3000 : false;
        },
        refetchOnWindowFocus: true,
        refetchOnMount: true,
      },
    },
  );

  const { mutateAsync: deleteImage } = useDeleteImageGenerator();

  const handleDelete = async (imageId: number) => {
    try {
      await deleteImage({ id: imageId });
      queryClient.invalidateQueries({
        queryKey: ["/image-generators/me"],
      });

      addToast({
        title: t("imagen.deleted"),
        variant: "success",
        duration: 3000,
      });
    } catch {
      addToast({
        title: t("imagen.deleteError"),
        description: t("imagen.deleteErrorDescription"),
        variant: "error",
      });
    }
  };

  const isGeneratedBefore = useMemo(() => {
    if (isLoading) {
      return true;
    }
    return !!allImages?.data?.some((a) => a.images?.length);
  }, [allImages, isLoading]);

  const LoadingState = () => (
    <div
      className="flex flex-col items-center justify-center min-h-[400px] space-y-4"
      data-testid="loading-dot-container"
    >
      <div className="w-16 h-16 border-4 border-purple-500 border-t-transparent rounded-full animate-spin"></div>
      <Text variant={"p"} className="text-gray-600 dark:text-gray-400">
        {t("imagen.loading")}
      </Text>
    </div>
  );

  const EmptyState = () => (
    <div className="flex flex-col items-center justify-center min-h-[400px] space-y-6 text-center">
      <div className="w-24 h-24 bg-purple-100 dark:bg-purple-900/30 rounded-full flex items-center justify-center">
        <div className="w-12 h-12 bg-purple-500 dark:bg-purple-400 rounded-full flex items-center justify-center">
          <Plus size={24} className="text-white" />
        </div>
      </div>
      <div className="space-y-2">
        <Text variant="h3" className="text-gray-900 dark:text-gray-100">
          {t("imagen.empty.title")}
        </Text>
        <Text variant="p" className="text-gray-600 dark:text-gray-400 max-w-md">
          {t("imagen.empty.description")}
        </Text>
      </div>
    </div>
  );

  return (
    <div className="flex-1 p-3 sm:p-4 md:p-6 lg:p-8 lg:mt-5 bg-gray-50 dark:bg-dark-500 lg:rounded-tl-3xl overflow-y-auto h-full custom-scrollbar">
      <div className="max-w-7xl mx-auto">
        {/* Header Section */}
        <div className="mb-4 sm:mb-6 lg:mb-8 flex flex-col lg:flex-row lg:items-center lg:justify-between gap-3 sm:gap-4">
          <div className="min-w-0 flex-1">
            <Text
              variant="h1"
              className="text-lg sm:text-xl lg:text-2xl xl:text-3xl break-words"
            >
              {t("imagen.title")}
            </Text>
            <Text
              variant="p"
              className="text-gray-600 dark:text-gray-400 mt-1 text-sm sm:text-base break-words"
            >
              {t("imagen.description")}
            </Text>
          </div>
        </div>

        {/* Create Image Section */}
        <div className="bg-white dark:bg-dark-600 rounded-xl shadow-sm border border-dark-200 dark:border-dark-400 p-3 sm:p-4 lg:p-6 mb-6">
          <Text
            variant="h2"
            className="mb-3 sm:mb-4 text-base sm:text-lg lg:text-xl xl:text-2xl break-words"
          >
            {t("imagen.create.title")}
          </Text>

          <CreateImage
            refetch={refetch}
            setSelectedImage={(image) => {
              setSelectedImage(image);
              setIsModalOpen(true);
            }}
            isGeneratedBefore={isGeneratedBefore}
          />
        </div>

        {isModalOpen && selectedImage && (
          <ImagePreviewModal
            images={selectedImage.images!}
            setIsModalOpen={setIsModalOpen}
          />
        )}

        {/* Image History Section */}
        {isLoading ? (
          <div className="bg-white dark:bg-dark-600 rounded-xl shadow-sm border border-dark-200 dark:border-dark-400 p-6">
            <LoadingState />
          </div>
        ) : error ? (
          <div className="bg-white dark:bg-dark-600 rounded-xl shadow-sm border border-dark-200 dark:border-dark-400 p-6">
            <ErrorComponent
              title={t("imagen.error.title")}
              refetch={refetch}
              description={t("imagen.error.description")}
              retryButton={t("imagen.error.retryButton")}
            />
          </div>
        ) : !allImages?.data?.length ? (
          <div className="bg-white dark:bg-dark-600 rounded-xl shadow-sm border border-dark-200 dark:border-dark-400 p-6">
            <EmptyState />
          </div>
        ) : (
          <div className="bg-white dark:bg-dark-600 rounded-xl shadow-sm border border-dark-200 dark:border-dark-400 p-3 sm:p-4 lg:p-6">
            <div className="mb-4 sm:mb-6">
              <Text
                variant="h2"
                className="text-base sm:text-lg lg:text-xl xl:text-2xl break-words"
              >
                {t("imagen.history.title")}
              </Text>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4 sm:gap-6">
              {allImages?.data?.map((item) => {
                const expectedImageCount = item.imageType === "DEFAULT" ? 4 : 1;
                const processingGridColumns =
                  expectedImageCount === 1
                    ? "grid-cols-1"
                    : "grid-cols-1 sm:grid-cols-2 md:grid-cols-2";

                return (
                  <div
                    key={item.id}
                    className="group bg-gray-50 dark:bg-dark-500 rounded-xl shadow-sm border border-gray-200 dark:border-dark-400 overflow-hidden transition-all duration-300 hover:shadow-lg hover:-translate-y-1"
                  >
                    <div className="p-4 sm:p-6 h-full relative">
                      <Button
                        variant="ghost"
                        size="icon"
                        rounded="xl"
                        className="absolute top-3 right-3 z-10 opacity-0 group-hover:opacity-100 transition-all duration-200"
                        bgColor="bg-white/90 dark:bg-dark-500/90 hover:bg-red-50 dark:hover:bg-red-900/50"
                        onClick={(e) => {
                          e.stopPropagation();
                          setShowDeletePopup(item.id);
                        }}
                      >
                        <Trash
                          size={16}
                          className="text-gray-400 dark:text-gray-500 group-hover:text-red-500 dark:group-hover:text-red-400 transition-colors"
                        />
                      </Button>

                      <div className="flex items-center justify-between mb-4">
                        <Text
                          variant="span"
                          className="text-xs text-gray-500 dark:text-gray-400"
                        >
                          {item.createdAt
                            ? format(new Date(item.createdAt), "PPP", {
                                locale: i18n.language === "tr" ? tr : enUS,
                              })
                            : ""}
                        </Text>
                      </div>

                      {/* Prompt Display */}
                      <div className="mb-4">
                        <div className="text-sm font-medium text-gray-700 dark:text-gray-200 mb-2">
                          {t("imagen.history.prompt")}:
                        </div>
                        {String(
                          (item.promptHistory && item.promptHistory[0]) || "",
                        ).length > 100 ? (
                          <div className="bg-white dark:bg-dark-600 rounded-lg p-3 border border-gray-200 dark:border-dark-400">
                            <span
                              id={`prompt-text-${item.id}`}
                              className="text-sm text-gray-600 dark:text-gray-300"
                            >
                              {String(
                                item.promptHistory && item.promptHistory[0],
                              ).slice(0, 100)}
                              ...
                            </span>
                            <Button
                              variant="ghost"
                              size="sm"
                              rounded="lg"
                              textColor="text-purple-500 hover:text-purple-600 dark:text-purple-400 dark:hover:text-purple-300"
                              className="ml-2"
                              onClick={(event) => {
                                const promptText = document.getElementById(
                                  `prompt-text-${item.id}`,
                                );
                                if (promptText) {
                                  promptText.textContent =
                                    (item.promptHistory &&
                                      item.promptHistory[0]) ||
                                    "";
                                  event.currentTarget.style.display = "none";
                                }
                              }}
                            >
                              {t("common.readMore")}
                            </Button>
                          </div>
                        ) : (
                          <div className="bg-white dark:bg-dark-600 rounded-lg p-3 border border-gray-200 dark:border-dark-400">
                            <span className="text-sm text-gray-600 dark:text-gray-300">
                              {String(
                                item.promptHistory && item.promptHistory[0],
                              )}
                            </span>
                          </div>
                        )}
                      </div>

                      {/* Enhanced Processing State */}
                      {(item.status !== "READY" ||
                        !item.images ||
                        item.images.length === 0) && (
                        <div
                          className={`mb-4 grid gap-4 ${processingGridColumns} ${expectedImageCount !== 1 ? "justify-items-center" : ""}`}
                        >
                          {Array.from({
                            length: expectedImageCount,
                          }).map((_, index) => (
                            <div
                              key={index}
                              className="relative aspect-square rounded-xl overflow-hidden w-full"
                            >
                              <div className="absolute inset-0 bg-gradient-to-r from-gray-200 via-gray-300 to-gray-200 dark:from-gray-700 dark:via-gray-600 dark:to-gray-700" />
                              <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent animate-shine-wave" />
                              <div className="absolute inset-0 bg-gradient-to-br from-white/10 to-black/5 dark:from-white/5 dark:to-black/20" />
                            </div>
                          ))}
                        </div>
                      )}

                      {/* Generated Images */}
                      {item.images &&
                        item.images.length > 0 &&
                        item.status === "READY" && (
                          <div
                            className={`grid gap-4 ${item.images.length === 1 ? "grid-cols-1" : "grid-cols-1 sm:grid-cols-2 md:grid-cols-2"} ${item.images.length !== 1 ? "justify-items-center" : ""}`}
                          >
                            {item.images.map((image, index) => {
                              const aspectRatio =
                                typeof image.aspectRatio === "string"
                                  ? image.aspectRatio
                                  : "1:1";
                              const aspectRatioClass = (() => {
                                switch (aspectRatio) {
                                  case "16:9":
                                    return "aspect-video";
                                  case "9:16":
                                    return "aspect-[9/16]";
                                  case "4:3":
                                    return "aspect-[4/3]";
                                  case "3:4":
                                    return "aspect-[3/4]";
                                  case "3:2":
                                    return "aspect-[3/2]";
                                  case "2:3":
                                    return "aspect-[2/3]";
                                  case "1:1":
                                  default:
                                    return "aspect-square";
                                }
                              })();

                              return (
                                <div
                                  key={index}
                                  onClick={() => {
                                    setSelectedImage(item);
                                    setIsModalOpen(true);
                                  }}
                                  className={`${aspectRatioClass} rounded-xl overflow-hidden relative group cursor-pointer transition-all duration-300 hover:scale-[1.02] hover:shadow-xl max-w-full w-full mx-auto max-h-[500px] border border-gray-200 dark:border-dark-400`}
                                >
                                  <img
                                    src={image.imageUrl || ""}
                                    alt={image.prompt || ""}
                                    className={`w-full h-full ${item.imageType === "DEFAULT" ? "object-cover" : "object-contain"} transition-transform duration-500 group-hover:scale-110`}
                                  />
                                  <div className="absolute inset-0 bg-gradient-to-t from-black/20 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />

                                  {/* Download Button */}
                                  <Button
                                    variant="solid"
                                    size="icon"
                                    rounded="lg"
                                    bgColor="bg-black/60 hover:bg-black/80 dark:bg-black/60 dark:hover:bg-black/80"
                                    textColor="text-white"
                                    className="absolute bottom-3 right-3 backdrop-blur-sm opacity-0 group-hover:opacity-100 hover:scale-110 active:scale-95 transition-all duration-200"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      const link = document.createElement("a");
                                      link.href = image.imageUrl || "";
                                      link.download = `generated-image-${item.id}-${index}.png`;
                                      link.click();
                                    }}
                                    aria-label={t("imagen.download.title")}
                                  >
                                    <DownloadIcon className="w-4 h-4" />
                                  </Button>
                                </div>
                              );
                            })}
                          </div>
                        )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>

      <ToolHelper
        toolPath={"images"}
        shouldShowTour={allImages?.data && allImages?.data?.length === 0}
        toolType={t(`sidebar.menu.toolbox.images`)}
      />

      {showDeletePopup && (
        <DeleteConfirmPopUp
          title={t("imagen.confirmDelete")}
          showDeletePopup={showDeletePopup}
          setShowDeletePopup={setShowDeletePopup}
          handleDelete={() => handleDelete(showDeletePopup)}
        />
      )}
    </div>
  );
};

export default ImageGeneratorView;
